import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackBusyCarouselEvent } from '@app/actions/advisors';
import classes from './classes.module.scss';
import AdvisorRecommendPreview from './advisor_recommend_preview';
import { liveModeState } from '../../../../config/const';
import AdvisorSection from '../../../home_page/nested_views/advisors_section';
import { advisorsSectionThemes, sectionIds } from '../../../home_page/prop_types';
import AdvisorSectionTitle from '../../../home_page/nested_views/advisors_section/advisor_section_title';

export interface AdvisorRecommendCarouselProps {
  loading: boolean;
  advisorId: number;
  advisors: Advisor.ListItem[];
  getSimilarOnlineAdvisors: (advisorId: number) => void;
  analytics: Analytic.Source;
  theme: string;
  userXfmProgram: Advisor.UserXfmProgramProps;
  trackBusyCarouselEvent: (eventName: string, advisors: Advisor.ListItem[]) => void;
}

const RECOMMENDED_ADVISORS_LIMIT = 5;

const AdvisorRecommendCarousel = ({
  loading,
  advisors = [],
  getSimilarOnlineAdvisors,
  advisorId,
  analytics,
  userXfmProgram
}: AdvisorRecommendCarouselProps) => {
  const { t } = useTranslation();
  const recommendedAdvisors = advisors.slice(0, RECOMMENDED_ADVISORS_LIMIT);

  useEffect(() => {
    if (advisors?.some((adv) => (adv.status === liveModeState.offline || adv.status === liveModeState.busy))) {
      getSimilarOnlineAdvisors(advisorId);
    }
  }, [advisors]);

  const trackBusyCarouselClickAction = () => trackBusyCarouselEvent('busy carousel clicked', recommendedAdvisors);

  const renderMobileCard = (adv: Advisor.ListItem, index: number) => (
    <AdvisorRecommendPreview
      key={ !loading ? adv.id : `emptyAdvisorSection_${ adv.id }` }
      advisor={ adv }
      loading={ adv.loading || loading }
      theme=""
      sectionSlug=""
      trackBEvent={ undefined }
      analytics={ analytics || { clickSource: 'busy carousel' } }
      userXfmProgram={ userXfmProgram }
      onAnalyticsClick={ trackBusyCarouselClickAction }
      sectionIndex={ 0 }
      itemIndex={ index }
    />
  );

  const renderAdvisorListSection = (props: Advisor.Section) => (
    <AdvisorSection
      id={ sectionIds.recommend_advisors }
      { ...props }
    />
  );

  const renderRecommendedAdvisors = () => (
    <div className={ classes.recommendedCarouselContainer }>
      <div className={ classes.recommendedCarousel }>
        { renderAdvisorListSection({
          theme: advisorsSectionThemes.recommend_advisors,
          advisors: recommendedAdvisors,
          loading,
          advisorAnalytics: analytics || { clickSource: 'busy carousel' },
          title: t('advisorProfile.try_these_advisors'),
          description: t('advisorProfile.explore_other_advisors'),
          onAnalyticsClick: trackBusyCarouselClickAction
        })}
      </div>
      <div className={ classes.recommendedCarouselMobile }>
        <AdvisorSectionTitle
          title={ t('advisorProfile.try_these_advisors') }
          shortDescription={ t('advisorProfile.explore_other_advisors') }
          loading={ loading }
          theme={ advisorsSectionThemes.recommend_advisors }
          iconUrl=""
          description=""
          showMore={ null }
          slug=""
          params={ {} }
        />
        <div className={ classes.recommendedAdvisors }>
          { recommendedAdvisors.map((adv: Advisor.ListItem, index) => renderMobileCard(adv, index)) }
        </div>
      </div>
    </div>
  );

  return renderRecommendedAdvisors();
};

export default AdvisorRecommendCarousel;
